import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Person, Role } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useFeatures } from 'utils/util/features';
import { useOrganizationSettings } from 'utils/util/hooks';
import AdvancedCurrentPeopleFilter from 'views/Widgets/Filters/AdvancedSpecializedFilter/AdvancedCurrentPeopleFilter';
import { useAdvancedCurrentPeopleFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter/context';
import {
  fromApiFilter,
  toApiFilter,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter/model';
import { TGroup } from 'views/Widgets/Filters/types';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditorButton from 'views/Widgets/Modals/ModalEditorButton';
import { PersonRole, PersonRolePostApi, RoleUtil } from './types';
import { useOrganizationCampaigns } from './useOrganizationCampaigns';

interface Props {
  buttonClassName?: string;
  buttonText: React.ReactNode | string;
  title: string;
  value?: PersonRole;
  roleName: string;
  role: Role;
  callback?: (object: PersonRole) => void;
}

const DEFAULT_CALLBACK = () => {};

const AdminRoleAddOrEditButton: FC<Props> = ({
  value,
  buttonClassName = 'btn btn-white',
  buttonText,
  title,
  roleName,
  role,
  callback = DEFAULT_CALLBACK,
}) => {
  const {
    withTargetAudience,
    withDevelopmentPlanTemplates,
    withTargetCampaigns,
    withCalibrationViewAdmin,
  } = RoleUtil.properties(role);
  const settings = useOrganizationSettings();
  const { campaigns } = useOrganizationCampaigns(withTargetCampaigns, {
    activeOnly: true,
  });
  const { isOptionEnabled } = useFeatures();
  const [object, setObject] = useState<ObjectState>(() =>
    defaultObjectState(role)
  );
  const proxyParameters = useProxyParameters();
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const handleChange = useCallback((newObject) => {
    setObject(newObject);
  }, []);

  const handleOnSubmit = useCallback(
    (submittedObject, onSubmit) => {
      ConfirmAPI.sendRequestToConfirm(
        object.id ? 'PATCH' : 'POST',
        object.id ? `/person-roles/${object.id}` : '/person-roles',
        toPersonRoleApiPayload(submittedObject, proxyParameters),
        (data, error: unknown, hardErrorMessage: unknown) => {
          onSubmit(data, error, hardErrorMessage);
        }
      );
    },
    [proxyParameters, object?.id]
  );

  const handleOnClosed = useCallback(() => {
    setIsOpen(false);
    setObject(defaultObjectState(role));
  }, [role]);

  const handleOnOpened = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleCallBack = useCallback(
    (data) => {
      callback(data);
      handleOnClosed();
    },
    [callback, handleOnClosed]
  );

  const { suggestions, selectedSuggestionConverter } =
    useAdvancedCurrentPeopleFilter();

  useEffect(() => {
    if (isOpen) {
      if (withTargetAudience && suggestions) {
        setObject((prev) => ({
          ...prev,
          ...value,
          target_audience: fromApiFilter(
            suggestions,
            selectedSuggestionConverter,
            value?.config?.target_audience
          ),
        }));
      }
      if (withDevelopmentPlanTemplates) {
        setObject((prev) => ({
          ...prev,
          ...value,
          target_development_plans:
            value?.config?.target_development_plans ?? [],
        }));
      }

      if (withTargetCampaigns) {
        setObject((prev) => ({
          ...prev,
          ...value,
          // CheckboxMulti keys are strings; ids are turned back into
          // numbers in toPersonRoleApiPayload
          target_campaigns: (value?.config?.target_campaigns ?? []).map(
            (campaignId) => campaignId.toString()
          ),
        }));
      }

      if (withCalibrationViewAdmin) {
        setObject((prev) => ({
          ...prev,
          ...value,
          calibration_view_admin:
            value?.config?.calibration_view_admin ?? false,
        }));
      }
    }
  }, [
    isOpen,
    selectedSuggestionConverter,
    suggestions,
    value,
    withDevelopmentPlanTemplates,
    withTargetAudience,
    withTargetCampaigns,
    withCalibrationViewAdmin,
  ]);

  return (
    <ModalEditorButton
      buttonClassName={buttonClassName}
      title={title}
      buttonText={buttonText}
      object={object}
      onChange={handleChange}
      submitPromise={handleOnSubmit}
      inputs={[
        {
          required: true,
          type: INPUT_TYPES.SELECT_PERSON,
          clearable: false,
          name: 'person',
          label: formatMessage({
            id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.person.label',
            defaultMessage: 'Person',
          }),
          helperText: formatMessage(
            {
              id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.person.helper_text',
              defaultMessage:
                'Select the person that will be assigned as {roleName}.',
            },
            { roleName }
          ),
          disabled: !!value,
        },
        ...(withTargetAudience
          ? [
              {
                type: INPUT_TYPES.CUSTOM_COMPONENT,
                component: AdvancedCurrentPeopleFilter,
                name: 'target_audience',
                label: formatMessage({
                  id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.target_audience.label',
                  defaultMessage: 'Target audience',
                }),
                helperText: formatMessage(
                  {
                    id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.target_audence.helper_text',
                    defaultMessage:
                      'Select the additional people that the {roleName} will be able to admin, in addition to the people below them in their chain of command. If you want this person to have visibility of everyone in the organization, leave this field empty.',
                  },
                  { roleName }
                ),
              },
            ]
          : []),
        ...(withDevelopmentPlanTemplates
          ? [
              {
                type: INPUT_TYPES.CHECKBOX_MULTI,
                name: 'target_development_plans',
                label: formatMessage({
                  id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.development_plan_templates.label',
                  defaultMessage: 'Development plan templates',
                }),
                helperText: formatMessage(
                  {
                    id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.development_plan_templates.helper_text',
                    defaultMessage:
                      'Select the templates that the {roleName} will be able to admin.',
                  },
                  { roleName }
                ),
                options: settings.development_plans_templates.map((entry) => ({
                  key: entry.id,
                  label: (
                    <span id={entry.id}>
                      {entry.name}
                      {entry.deprecated
                        ? ` - ${formatMessage({
                            id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.development_plan_templates.deprecated',
                            defaultMessage: 'Deprecated',
                          })}`
                        : ''}
                      {entry.candidate_can_manage
                        ? ` - ${formatMessage({
                            id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.development_plan_templates.candidate_can_manage',
                            defaultMessage: 'Candidate can manage',
                          })}`
                        : ''}
                    </span>
                  ),
                })),
              },
            ]
          : []),
        ...(withTargetCampaigns
          ? [
              {
                type: INPUT_TYPES.CHECKBOX_MULTI,
                name: 'target_campaigns',
                label: formatMessage({
                  id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.target_campaigns.label',
                  defaultMessage: 'Cycles',
                }),
                helperText: formatMessage(
                  {
                    id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.target_campaigns.helper_text',
                    defaultMessage:
                      'Select the cycles whose calibration views the {roleName} will be able to edit. If you want this person to edit the calibration views of every cycle, leave this field empty.',
                  },
                  { roleName }
                ),
                options: campaigns.map((campaign) => ({
                  key: campaign.id.toString(),
                  label: campaign.name,
                })),
              },
            ]
          : []),
        ...(withCalibrationViewAdmin &&
        isOptionEnabled('performance', 'calibration_dashboard')
          ? [
              {
                type: INPUT_TYPES.SWITCH,
                name: 'calibration_view_admin',
                label: formatMessage({
                  id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.calibration_admin.label',
                  defaultMessage: 'Calibration view admin',
                }),
                helperText: formatMessage({
                  id: 'app.views.administration.people_administration.administrators.admin_role_add_or_edit_button.inputs.calibration_admin.helper_text',
                  defaultMessage:
                    'Allows the person to manage all calibration views.',
                }),
              },
            ]
          : []),
      ]}
      callback={handleCallBack}
      onClosed={handleOnClosed}
      onOpened={handleOnOpened}
    />
  );
};

interface ObjectState {
  id?: number;
  person?: Person;
  role: Role;
  target_audience?: TGroup;
  target_development_plans?: string[];
  // campaign ids as strings, see CheckboxMulti
  target_campaigns?: string[];
  calibration_view_admin?: boolean;
}

const defaultObjectState = (role: Role): ObjectState => ({
  role,
  person: undefined,
  target_audience: undefined,
  calibration_view_admin: undefined,
});

const toPersonRoleApiPayload = (
  value: ObjectState,
  proxyParameters
): PersonRolePostApi => {
  return {
    ...value,
    ...proxyParameters,
    config: {
      target_audience: toApiFilter(value.target_audience),
      target_development_plans: value.target_development_plans,
      target_campaigns: value.target_campaigns?.map((campaignId) =>
        parseInt(campaignId, 10)
      ),
      calibration_view_admin: value.calibration_view_admin,
    },
    person: value.person!.id,
  };
};

export default React.memo(AdminRoleAddOrEditButton);
